/**
 * Helpers for rendering "document badge" spans inside AI chat messages, and
 * for rewriting LLM-emitted anchor tags as those same badges so clicks are
 * routed through the in-app viewer instead of navigating to a gray route.
 */

export interface DocumentBadgeInput {
  uuid?: string | null
  title?: string | null
  pages?: Array<number | string>
}

export type DocumentRegistryEntry = DocumentBadgeInput & { _key: string }

export const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const BADGE_CLASSES =
  'document-link inline-flex cursor-pointer items-center gap-x-1 rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800 dark:text-white'

export function renderDocumentBadge(
  key: string,
  title: string,
  iconHtml: string,
  pageText = ''
): string {
  const pageSuffix = pageText ? ` <span class="text-gray-500">(${pageText})</span>` : ''
  return `<span class="${BADGE_CLASSES}" data-ref="${key}">${iconHtml}${escapeHtml(title)}${pageSuffix}</span>`
}

// Full UUID v4 shape (8-4-4-4-12 hex). The Langfuse chat prompt instructs the
// model to emit full UUIDs, so this is the expected input shape. We still
// reject truncated UUIDs defensively: in a legal product, opening the wrong
// document via a prefix collision is worse than not converting the anchor.
const DOCUMENT_URL_UUID_RE =
  /\/documents\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i

// md.render() emits well-formed anchors with double-quoted href and no nested
// <a> tags, so a regex is sufficient here — we are not parsing arbitrary
// user HTML. The unit tests feed real md.render() output through to guard
// this assumption against future markdown-it config changes.
const ANCHOR_RE = /<a\s[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi

function extractDocumentUuid(href: string): string | null {
  if (href.startsWith('#doc-')) return href.slice('#doc-'.length)
  const m = href.match(DOCUMENT_URL_UUID_RE)
  return m ? m[1] : null
}

/**
 * Rewrite `<a href="/documents/<uuid>">` and `<a href="#doc-<uuid>">` anchors
 * inside rendered-markdown HTML as in-app document badges.
 *
 * The LLM sometimes emits real anchor tags instead of the structured
 * `document` chunk. Without this, clicking the anchor navigates to a route
 * that renders as a gray screen (AI-340).
 *
 * `onMissing` is invoked when the UUID is not in the registry. The full chat
 * renderer uses it to synthesise a registry entry so badges still render for
 * docs the model referenced but which were not in the chunk list; the
 * dashboard widget omits it so unknown refs pass through unchanged.
 */
export function rewriteDocumentAnchorsAsBadges(opts: {
  html: string
  registry: Map<string, DocumentRegistryEntry>
  renderIcon: () => string
  onMissing?: (uuid: string, title: string) => DocumentRegistryEntry | null
}): string {
  const { html, registry, renderIcon, onMissing } = opts
  return html.replace(ANCHOR_RE, (match, href, innerText) => {
    const uuid = extractDocumentUuid(href)
    if (!uuid) return match
    // Strip inline formatting the model may have nested inside the link text.
    const plainText = innerText.replace(/<[^>]*>/g, '').trim()
    const existing = registry.get(uuid)
    if (existing) {
      return renderDocumentBadge(existing._key, plainText || existing.title || uuid, renderIcon())
    }
    const synthesised = onMissing?.(uuid, plainText || uuid)
    if (synthesised) {
      return renderDocumentBadge(synthesised._key, synthesised.title || uuid, renderIcon())
    }
    return match
  })
}
